<script setup lang="ts">
/**
 * THE SAGA GUIDE — CU-86cb1qvpt items 2+5: a standalone "why play & how it
 * works" page, reachable at any time (the integrator wires the nav entry).
 * Pure content: no fetches, no server state — it is a read of static prose,
 * the same footing as FaqPanel/LegalPanel's admin-content viewers, except this
 * copy belongs to the game itself rather than the CMS.
 *
 * Scaffold borrowed from Rankings.vue / Dungeons.vue (`.scr-head` banner) and
 * Settings.vue (`.ob-sec` section rhythm + gilt-text dividers) — this reads as
 * a page in the same book as every other screen, not a bolt-on modal.
 */
import { t } from '../i18n';
import { BG } from '../game/art';
import { openTutorial } from '../game/tutorial-launch';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import mobArt from '../assets/fimbulwake/mobs/band-leader.webp';
import wardArt from '../assets/fimbulwake/districts/docks.webp';
import bust1 from '../assets/fimbulwake/ranks/bust-1.webp';
import bust2 from '../assets/fimbulwake/ranks/bust-2.webp';
import bust3 from '../assets/fimbulwake/ranks/bust-3.webp';
import bust4 from '../assets/fimbulwake/ranks/bust-4.webp';
import bust5 from '../assets/fimbulwake/ranks/bust-5.webp';
import bust6 from '../assets/fimbulwake/ranks/bust-6.webp';
import weaponIcon from '../assets/fimbulwake/items/named/bearded-axe.webp';

/** The six rungs of the ladder, pictured — same busts the landing shows. */
const RANKS = [
  { art: bust1, get name() { return t('guide.ranks.r1'); } },
  { art: bust2, get name() { return t('guide.ranks.r2'); } },
  { art: bust3, get name() { return t('guide.ranks.r3'); } },
  { art: bust4, get name() { return t('guide.ranks.r4'); } },
  { art: bust5, get name() { return t('guide.ranks.r5'); } },
  { art: bust6, get name() { return t('guide.ranks.r6'); } },
];

/** One picture per loop step — the same art the screens themselves open with. */
const LOOP_ART = [BG.jobs, BG.market, mobArt, wardArt, BG.pit];

/** The life of an item, step by step, with the wear bar each state shows. */
const GEAR_STEPS = [
  { key: 'buy', bar: '100%' },
  { key: 'wear', bar: '56%' },
  { key: 'broken', bar: '0%' },
  { key: 'repair', bar: '100%' },
] as const;

const emit = defineEmits<{ (e: 'back'): void }>();
</script>

<template>
  <section class="sg-guide">
    <!-- banner — same lintel every screen opens with -->
    <header class="scr-head">
      <span class="scr-head-win"><img :src="BG.study" alt="" /></span>
      <div class="scr-head-txt">
        <h1>{{ t('guide.title') }}</h1>
        <p>{{ t('guide.subtitle') }}</p>
      </div>
    </header>

    <div class="sg-actions">
      <button class="btn-forge btn-forge--carved btn-forge--sm sg-back" @click="emit('back')">
        &larr; {{ t('guide.back') }}
      </button>
      <!-- QA punch-list 07.08 item 1: the 9-step onboarding, replayable at will -->
      <button class="btn-forge btn-forge--rune btn-forge--sm" @click="openTutorial()">
        {{ t('guide.replay') }}
      </button>
    </div>

    <div class="sg-body surf-plank bound-frame">

      <!-- ══ 1. THE GOAL ══ -->
      <div class="sg-sec">
        <h2 class="gilt-text">{{ t('guide.goal.heading') }}</h2>
        <p class="sg-p">{{ t('guide.goal.body') }}</p>
        <!-- the ladder itself, pictured — the same six busts the landing shows -->
        <div class="sg-ranks" aria-hidden="true">
          <figure v-for="(r, i) in RANKS" :key="i" class="sg-rank" :class="{ 'sg-rank--last': i === RANKS.length - 1 }">
            <img :src="r.art" alt="" loading="lazy" />
            <figcaption>{{ r.name }}</figcaption>
          </figure>
        </div>
      </div>

      <!-- ══ 2. THE LOOP ══ -->
      <div class="sg-sec">
        <h2 class="gilt-text">{{ t('guide.loop.heading') }}</h2>
        <p class="sg-p">{{ t('guide.loop.intro') }}</p>
        <ol class="sg-loop">
          <li v-for="n in 5" :key="n">
            <span class="sg-loop-art" aria-hidden="true"><img :src="LOOP_ART[n - 1]" alt="" loading="lazy" /></span>
            <div class="sg-loop-txt">
              <b>{{ t(`guide.loop.step${n}.title`) }}</b>
              <span>{{ t(`guide.loop.step${n}.body`) }}</span>
            </div>
          </li>
        </ol>
      </div>

      <!-- ══ 3. SCREENS MAP ══ -->
      <div class="sg-sec">
        <h2 class="gilt-text">{{ t('guide.screens.heading') }}</h2>
        <p class="sg-p">{{ t('guide.screens.intro') }}</p>
        <div class="sg-table">
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.map.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.map.body') }}</span>
          </div>
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.wards.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.wards.body') }}</span>
          </div>
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.errands.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.errands.body') }}</span>
          </div>
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.market.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.market.body') }}</span>
          </div>
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.holmgang.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.holmgang.body') }}</span>
          </div>
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.raids.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.raids.body') }}</span>
          </div>
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.inventory.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.inventory.body') }}</span>
          </div>
          <div class="sg-row">
            <span class="sg-row-name">{{ t('guide.screens.settings.title') }}</span>
            <span class="sg-row-desc">{{ t('guide.screens.settings.body') }}</span>
          </div>
        </div>
      </div>

      <!-- ══ 4. ECONOMY ══ -->
      <div class="sg-sec">
        <h2 class="gilt-text">{{ t('guide.economy.heading') }}</h2>
        <div class="sg-coin-grid">
          <div class="sg-coin">
            <div class="sg-coin-name"><img :src="cashIcon" alt="" class="sg-coin-ic" />{{ t('guide.economy.silver.title') }}</div>
            <p class="sg-p">{{ t('guide.economy.silver.body') }}</p>
          </div>
          <div class="sg-coin">
            <div class="sg-coin-name sg-coin-name--gold"><img :src="runesIcon" alt="" class="sg-coin-ic" />{{ t('guide.economy.runes.title') }}</div>
            <p class="sg-p">{{ t('guide.economy.runes.body') }}</p>
          </div>
        </div>
        <p class="sg-p sg-note">{{ t('guide.economy.repair') }}</p>
      </div>

      <!-- ══ 5. WHAT YOU CAN LOSE ══ -->
      <div class="sg-sec">
        <h2 class="gilt-text">{{ t('guide.risk.heading') }}</h2>
        <p class="sg-p">{{ t('guide.risk.intro') }}</p>
        <ul class="sg-risk-list">
          <li>
            <b>{{ t('guide.risk.fury.title') }}</b>
            <span>{{ t('guide.risk.fury.body') }}</span>
          </li>
          <li>
            <b>{{ t('guide.risk.cooldown.title') }}</b>
            <span>{{ t('guide.risk.cooldown.body') }}</span>
          </li>
          <li>
            <b>{{ t('guide.risk.duel.title') }}</b>
            <span>{{ t('guide.risk.duel.body') }}</span>
          </li>
        </ul>
        <div class="st-plate st-plate--green sg-gear-plate">
          <span>{{ t('guide.risk.gear') }}</span>
        </div>
      </div>

      <!-- ══ 5b. THE MOVES & FURY — what each strike does and what it burns
           (QA 15.08 asked for the guide to cover every strike type, plus an
           explanation of how the fury economy works) ══ -->
      <div class="sg-sec">
        <h2 class="gilt-text">{{ t('guide.moves.heading') }}</h2>
        <p class="sg-p">{{ t('guide.moves.intro') }}</p>
        <ul class="sg-risk-list">
          <li v-for="k in (['strike', 'heavy', 'guard', 'ability', 'flee', 'heal'] as const)" :key="k">
            <b>{{ t(`guide.moves.${k}.title`) }}</b>
            <span>{{ t(`guide.moves.${k}.body`) }}</span>
          </li>
        </ul>
        <p class="sg-p sg-note">{{ t('guide.moves.horn') }}</p>
      </div>

      <!-- ══ 6. GEAR — the life of an item: buy, wear, break, repair ══ -->
      <div class="sg-sec sg-sec--last">
        <h2 class="gilt-text">{{ t('guide.gear.heading') }}</h2>
        <p class="sg-p">{{ t('guide.gear.intro') }}</p>
        <ol class="sg-gear">
          <li v-for="(step, i) in GEAR_STEPS" :key="step.key" :class="{ 'sg-gear--broken': step.key === 'broken' }">
            <span class="sg-gear-fig" aria-hidden="true">
              <img :src="weaponIcon" alt="" :class="{ 'is-dead': step.key === 'broken' }" />
              <span class="sg-gear-bar" :class="{ 'is-dead': step.key === 'broken' }"><i :style="{ width: step.bar }"></i></span>
            </span>
            <div class="sg-gear-txt">
              <b>{{ i + 1 }}. {{ t(`guide.gear.${step.key}.title`) }}</b>
              <span>{{ t(`guide.gear.${step.key}.body`) }}</span>
            </div>
          </li>
        </ol>
        <p class="sg-p sg-note">{{ t('guide.gear.costs') }}</p>
      </div>

      <!-- ══ 7. CLANS — what a banner does and does not do today ══ -->
      <div class="sg-sec">
        <h2 class="gilt-text">{{ t('guide.clans.heading') }}</h2>
        <p class="sg-p">{{ t('guide.clans.intro') }}</p>
        <ul class="sg-risk-list">
          <li>
            <b>{{ t('guide.clans.found.title') }}</b>
            <span>{{ t('guide.clans.found.body') }}</span>
          </li>
          <li>
            <b>{{ t('guide.clans.points.title') }}</b>
            <span>{{ t('guide.clans.points.body') }}</span>
          </li>
          <li>
            <b>{{ t('guide.clans.wars.title') }}</b>
            <span>{{ t('guide.clans.wars.body') }}</span>
          </li>
        </ul>
      </div>

    </div>
  </section>
</template>

<style scoped>
/* ── the back rail — a plain carved control above the book, matching the
   "back" affordance every other full-screen view in the shell exposes. ── */
.sg-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
.sg-back { flex: none; }

/* ── THE BOOK — one plank, same grammar as Settings' ledger page: gilt-text
   section headings with the rune divider, sections separated by a hairline
   rhythm, no boxed cards. ── */
.sg-body { padding: 22px 26px 26px; }
.sg-sec { padding-bottom: 8px; }
.sg-sec:not(.sg-sec--last) {
  border-bottom: 2px solid transparent;
  background:
    linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)) bottom / 100% 1px no-repeat,
    linear-gradient(rgba(207, 232, 255, 0.1), rgba(207, 232, 255, 0.1)) bottom -1px / 100% 1px no-repeat;
  margin-bottom: 18px;
}
.sg-sec > h2 {
  font-family: var(--font-heading, serif);
  font-size: 19px; letter-spacing: 0.02em;
  margin-bottom: 6px;
}
.sg-sec > h2::after {
  content: '';
  display: block;
  width: 200px; height: 9px;
  margin-top: 7px; margin-bottom: 14px;
  background: var(--orn-divider-gold) no-repeat left center / contain;
}
.sg-p {
  font-family: var(--font-body, serif); font-size: 14px; line-height: 1.6;
  color: var(--color-text);
  max-width: 68ch;
}
.sg-note { font-style: italic; color: var(--color-text-dim); font-size: 13px; margin-top: 10px; }

/* ── the rank ladder, pictured — six busts climbing left to right, the last
   one (Konungr) rimmed gold. Pure ornament: aria-hidden, names repeated in
   the prose above. ── */
.sg-ranks {
  display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px;
  margin-top: 14px;
  max-width: 640px;
}
.sg-rank { margin: 0; text-align: center; }
.sg-rank img {
  width: 100%; aspect-ratio: 1; object-fit: cover;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}
.sg-rank--last img { box-shadow: var(--rim-ice-sunken), inset 0 0 0 1.5px color-mix(in srgb, var(--color-gold) 60%, transparent); }
.sg-rank figcaption {
  margin-top: 5px;
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.sg-rank--last figcaption { color: var(--color-gold-bright); }

/* ── the loop — numbered by the ordered list marker itself, each step now
   carrying the art of the screen it points at. ── */
.sg-loop {
  display: flex; flex-direction: column; gap: 10px;
  margin-top: 6px;
  padding-left: 0;
  list-style: none;
  counter-reset: sg-step;
}
.sg-loop > li {
  position: relative;
  counter-increment: sg-step;
  display: flex; align-items: center; gap: 14px;
  padding: 10px 14px 10px 44px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}
.sg-loop > li::before {
  content: counter(sg-step);
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  font-family: var(--font-heading, serif); font-size: 16px;
  color: var(--color-gold-bright);
}
.sg-loop-art {
  flex: 0 0 auto; width: 74px; height: 54px; overflow: hidden;
  clip-path: var(--clip-hewn);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.55);
}
.sg-loop-art img { width: 100%; height: 100%; object-fit: cover; }
.sg-loop-txt { min-width: 0; }
.sg-loop-txt > b {
  display: block;
  font-family: var(--font-heading, serif); font-size: 14.5px;
  color: var(--color-gold);
  margin-bottom: 2px;
}
.sg-loop-txt > span {
  font-family: var(--font-body, serif); font-size: 13px; color: var(--color-text-dim);
  line-height: 1.5;
}

/* ── screens map — a ledger row per screen, same measure as Settings' st-row
   but with the description sharing the line on wide viewports. ── */
.sg-table { display: flex; flex-direction: column; margin-top: 4px; }
.sg-row {
  /* 172px, not 160px: de/hu screen-name strings ("Die Feste (Karte)") sit
     within a few px of a tight 160px nowrap column across shipped locales */
  display: grid; grid-template-columns: 172px minmax(0, 1fr);
  gap: 16px; align-items: baseline;
  padding: 10px 0;
  position: relative;
}
.sg-row:not(:last-child)::after {
  content: '';
  position: absolute; left: 0; right: 0; bottom: 0; height: 2px;
  background:
    linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)) top / 100% 1px no-repeat,
    linear-gradient(rgba(207, 232, 255, 0.12), rgba(207, 232, 255, 0.12)) bottom / 100% 1px no-repeat;
}
.sg-row-name {
  font-family: var(--font-heading, serif); font-size: 14.5px;
  color: var(--color-gold);
  white-space: nowrap;
}
.sg-row-desc {
  font-family: var(--font-body, serif); font-size: 13px; line-height: 1.5;
  color: var(--color-text);
}

/* ── economy — two coin plaques side by side, each its own chisel slot. ── */
.sg-coin-grid {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px;
  margin-top: 4px;
}
.sg-coin {
  padding: 14px 16px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}
.sg-coin-name {
  display: flex; align-items: center; gap: 7px;
  font-family: var(--font-label, sans-serif); font-size: 11px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #bccbdb; margin-bottom: 6px;
}
.sg-coin-ic { width: 18px; height: 18px; object-fit: contain; flex: none; }
.sg-coin-name--gold { color: var(--color-gold-bright); }
.sg-coin .sg-p { font-size: 13px; max-width: none; }

/* ── what's at stake — a plain list, then the one reassuring plate about
   gear reusing Settings' st-plate grammar so it reads as a genuine STATUS
   line rather than another bullet. ── */
.sg-risk-list {
  display: flex; flex-direction: column; gap: 10px;
  margin: 4px 0 16px;
  padding-left: 0;
  list-style: none;
}
.sg-risk-list > li {
  padding-left: 14px;
  border-left: 2px solid color-mix(in srgb, var(--color-ember) 55%, transparent);
}
.sg-risk-list > li > b {
  display: block;
  font-family: var(--font-heading, serif); font-size: 14px; color: var(--color-text);
  margin-bottom: 2px;
}
.sg-risk-list > li > span {
  font-family: var(--font-body, serif); font-size: 13px; color: var(--color-text-dim); line-height: 1.5;
}
.sg-gear-plate {
  position: relative;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px 10px 20px; margin-bottom: 0;
  font-family: var(--font-body, serif); font-size: 13.5px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}

/* ── the life of an item — four ledger rows, each carrying the wear bar the
   game itself draws (iron channel, ember fill; dead = red-rimmed empty). ── */
.sg-gear {
  display: flex; flex-direction: column; gap: 10px;
  margin: 10px 0 14px;
  padding-left: 0;
  list-style: none;
}
.sg-gear > li {
  display: flex; align-items: center; gap: 14px;
  padding: 10px 14px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}
.sg-gear--broken {
  box-shadow: var(--rim-ice-sunken), inset 0 0 0 1px color-mix(in srgb, var(--color-ember) 40%, transparent);
}
.sg-gear-fig { flex: 0 0 64px; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.sg-gear-fig img { width: 44px; height: 44px; object-fit: contain; filter: drop-shadow(0 6px 8px rgba(0, 0, 0, 0.7)); }
.sg-gear-fig img.is-dead { filter: grayscale(0.75) brightness(0.7) drop-shadow(0 6px 8px rgba(0, 0, 0, 0.7)); }
.sg-gear-bar {
  display: block; width: 56px; height: 6px;
  background: linear-gradient(180deg, #0a0e16, #131a24);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.8), inset 0 0 0 1px rgba(0, 0, 0, 0.6);
}
.sg-gear-bar i { display: block; height: 100%; background: linear-gradient(90deg, #7fbf8f, #a8d99f); }
.sg-gear-bar.is-dead { box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.8), inset 0 0 0 1px color-mix(in srgb, var(--color-ember) 55%, transparent); }
.sg-gear-txt { min-width: 0; }
.sg-gear-txt > b {
  display: block;
  font-family: var(--font-heading, serif); font-size: 14.5px; color: var(--color-gold);
  margin-bottom: 2px;
}
.sg-gear-txt > span {
  font-family: var(--font-body, serif); font-size: 13px; color: var(--color-text-dim); line-height: 1.5;
}


/* ── phone: the two-column screens rows and the coin grid both collapse to
   one column — nothing here needs sideways scroll. ── */
@media (max-width: 640px) {
  .sg-body { padding: 16px 14px 18px; }
  .sg-row { grid-template-columns: 1fr; gap: 2px; }
  .sg-row-name { white-space: normal; }
  .sg-coin-grid { grid-template-columns: 1fr; }
  .sg-ranks { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .sg-loop-art { width: 56px; height: 44px; }
}
</style>
